import { endOfDay, startOfDay, subDays } from 'date-fns';
import { useCallback, useEffect, useState } from 'react';
import DateRangePicker, { RangeType } from 'rsuite/DateRangePicker';
import 'rsuite/DateRangePicker/styles/index.css';
import { InsightFilterRow } from 'src/components/insights/InsightFilterRow';
import { coerceAndValidateRight } from 'src/components/insights/insightFilterRules';
import {
  AutoCompleteMultiSelectBox,
  Option,
} from 'src/components/AutoCompleteMultiSelectBox';
import { AutoCompleteSelectBox } from 'src/components/AutoCompleteSelectBox';
import { Button } from 'src/components/Button';
import { Input } from 'src/components/Input';
import { Label } from 'src/components/Label';
import { Modal } from 'src/components/Modal';
import { SelectBox } from 'src/components/SelectBox';
import { Tooltip } from 'src/components/Tooltip';
import { useNotification } from 'src/contexts/NotificationContext';
import useAuthedRequest from 'src/hooks/useAuthedRequest';
import { Action } from 'src/interfaces/action.interface';
import {
  Agent,
  FieldOperators,
  GroupOperators,
  Insight,
  InsightType,
} from 'src/interfaces/agent.interface';
import { CallFilters } from 'src/interfaces/call.interface';
import { useInsights } from 'src/pages/Agents/contexts/InsightsContext';
import { statusesOptions } from 'src/stubs/calls.stub';
import { getFirstAndLastFourUUID } from 'src/utils/number';
import { v4 as uuidv4 } from 'uuid';

import { Info, Plus, X } from '@phosphor-icons/react';
import { Campaign } from 'src/services/campaigns/campaigns.interface';
import './style.css';

class InvalidInsightFiltersError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'InvalidInsightFiltersError';
  }
}

type InsightFilterRowState = {
  id: string;
  insight_id: string;
  operator: FieldOperators | undefined;
  right: string | boolean | null;
};

interface CallFiltersModalProps {
  isOpen: boolean;
  onClose: () => void;
  filters: CallFilters;
  onApplyFilters: (filters: CallFilters) => void;
  agents: Agent[];
  campaigns: Campaign[];
  searchAgents: (name: string) => Promise<{ value: string; label: string }[]>;
  searchCampaigns: (
    name: string,
  ) => Promise<{ value: string; label: string }[]>;
  loading: boolean;
}

const durationComparisonOptions = [
  { value: 'gt', label: '>' },
  { value: 'gte', label: '>=' },
  { value: 'lt', label: '<' },
  { value: 'lte', label: '<=' },
  { value: 'eq', label: '=' },
];

export const CallFiltersModal = ({
  isOpen,
  onClose,
  filters,
  onApplyFilters,
  agents,
  campaigns,
  searchAgents,
  searchCampaigns,
  loading,
}: CallFiltersModalProps) => {
  const notification = useNotification();
  const { listInsights } = useInsights();
  const [localFilters, setLocalFilters] = useState<CallFilters>(filters);
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>(() => {
    const end = endOfDay(new Date());
    const start = startOfDay(subDays(end, 27));
    return [start, end];
  });
  const [actions, setActions] = useState<Action[]>([]);
  const [actionsLoading, setActionsLoading] = useState(false);
  const [callContextRows, setCallContextRows] = useState<
    Array<{ id: string; key: string; value: string }>
  >([{ id: uuidv4(), key: '', value: '' }]);
  const [insightFilterRows, setInsightFilterRows] = useState<
    InsightFilterRowState[]
  >([]);
  const [insights, setInsights] = useState<Insight[]>([]);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const { get } = useAuthedRequest();

  const agentsOptions = [
    { value: 'All Agents' },
    ...agents.map((agent: Agent) => ({
      value: agent.id as string,
      label: agent.name || getFirstAndLastFourUUID(agent.id as string),
    })),
  ];

  const campaignOptions = [
    { value: 'All Campaigns' },
    ...campaigns.map((campaign) => ({
      value: campaign.referenceId,
      label: campaign.name
        ? `${campaign.name} (${getFirstAndLastFourUUID(campaign.referenceId)})`
        : getFirstAndLastFourUUID(campaign.referenceId),
    })),
  ];

  const customRanges = [
    {
      label: 'Last 28 days',
      value: [startOfDay(subDays(new Date(), 27)), endOfDay(new Date())],
      closeOverlay: true,
    },
    {
      label: 'Last 7 days',
      value: [startOfDay(subDays(new Date(), 6)), endOfDay(new Date())],
      closeOverlay: true,
    },
    {
      label: 'Yesterday',
      value: [
        startOfDay(subDays(new Date(), 1)),
        endOfDay(subDays(new Date(), 1)),
      ],
      closeOverlay: true,
    },
    {
      label: 'Today',
      value: [startOfDay(new Date()), endOfDay(new Date())],
      closeOverlay: true,
    },
  ];

  const fetchActions = useCallback(
    async (query?: string) => {
      setActionsLoading(true);
      try {
        const queryParams = new URLSearchParams({
          size: '50',
          filters: JSON.stringify({
            scope: 'agent',
            ...(query && query.length > 0 && { label: query }),
          }),
        });

        const { items } = await get(`/actions?${queryParams.toString()}`);
        setActions(items);
      } catch (error) {
        console.error('Error fetching actions:', error);
      } finally {
        setActionsLoading(false);
      }
    },
    [get],
  );

  const actionOptions: Option[] = actions.map((action) => ({
    value: JSON.stringify({ type: action.type, label: action.label }),
    label: action.label || action.type,
  }));

  const selectedActionOptions: Option[] = (
    localFilters.successful_actions || []
  ).map((action) => ({
    value: JSON.stringify({ type: action.type, label: action.label }),
    label: action.label || action.type,
  }));

  const handleSuccessfulActionsChange = (options: Option[]) => {
    const successfulActions = options.map((option) => {
      const parsed = JSON.parse(option.value);
      return { type: parsed.type, label: parsed.label };
    });
    updateLocalFilters({ successful_actions: successfulActions });
  };

  const getInsightById = (insightId: string) =>
    insights.find((insight) => insight.id === insightId);

  const coerceInsightFilterRightForApi = (
    row: InsightFilterRowState,
  ): string | number | boolean => {
    const insight = getInsightById(row.insight_id);
    if (!insight) {
      throw new InvalidInsightFiltersError(
        'One or more insight filters reference an unknown insight. Remove and re-add those rows.',
      );
    }

    const coercionResult = coerceAndValidateRight({
      item: insight,
      operator: row.operator,
      rightInput: row.right,
    });
    if (!coercionResult.ok) {
      throw new InvalidInsightFiltersError(
        `${insight.label || insight.id}: ${coercionResult.message}`,
      );
    }

    return coercionResult.right;
  };

  const getPostCallInsightsFilterForApi = ():
    | CallFilters['post_call_insights']
    | undefined => {
    if (insightFilterRows.length === 0) return undefined;

    const hasIncompleteRow = insightFilterRows.some((row) => {
      if (!row.insight_id.trim()) return true;
      if (!row.operator) return true;
      if (row.right === null) return true;
      if (typeof row.right === 'string' && row.right.trim() === '') return true;
      return false;
    });

    if (hasIncompleteRow) {
      throw new InvalidInsightFiltersError(
        'Please complete all insight filters (insight, operator, and value) or remove incomplete rows.',
      );
    }

    return {
      group_operator: GroupOperators.AND,
      children: insightFilterRows.map((row) => ({
        insight_id: row.insight_id.trim(),
        operator: row.operator!,
        value: coerceInsightFilterRightForApi(row),
      })),
    };
  };

  const addInsightFilterRow = () => {
    setInsightFilterRows((previousRows) => [
      ...previousRows,
      {
        id: uuidv4(),
        insight_id: '',
        operator: undefined,
        right: null,
      },
    ]);
  };

  const updateInsightFilterRow = (
    rowId: string,
    updates: Partial<Omit<InsightFilterRowState, 'id'>>,
  ) => {
    setInsightFilterRows((previousRows) =>
      previousRows.map((row) =>
        row.id === rowId
          ? {
              ...row,
              ...updates,
            }
          : row,
      ),
    );
  };

  const removeInsightFilterRow = (rowId: string) => {
    setInsightFilterRows((previousRows) =>
      previousRows.filter((row) => row.id !== rowId),
    );
  };

  const handleApply = () => {
    const callContextJson = getCallContextJson();
    let postCallInsights: CallFilters['post_call_insights'] | undefined;
    try {
      postCallInsights = getPostCallInsightsFilterForApi();
    } catch (error) {
      notification.error(
        error instanceof Error
          ? error.message
          : 'Insight filters are invalid. Please update or remove those rows.',
      );
      return;
    }

    const updatedFilters: CallFilters = {
      ...localFilters,
      call_context:
        Object.keys(callContextJson).length > 0 ? callContextJson : undefined,
      post_call_insights: postCallInsights,
      ...(dateRange &&
      dateRange[0] instanceof Date &&
      dateRange[1] instanceof Date
        ? {
            start_time: {
              comparison: 'btwn',
              value: [dateRange[0].toISOString(), dateRange[1].toISOString()],
            },
          }
        : { start_time: null }),
    };
    onApplyFilters(updatedFilters);
    onClose();
  };

  const handleReset = () => {
    const end = endOfDay(new Date());
    const start = startOfDay(subDays(end, 27));
    const resetFilters: CallFilters = {
      agent: 'All Agents',
      workflow_reference_id: 'All Campaigns',
      status: 'All Statuses',
      call_id: '',
      contact_number: '',
      from_number: '',
      to_number: '',
      start_time: {
        comparison: 'btwn',
        value: [start.toISOString(), end.toISOString()],
      },
      duration: undefined,
      successful_actions: [],
      transcript_keyword: '',
      call_context: undefined,
      post_call_insights: undefined,
    };
    setLocalFilters(resetFilters);
    setDateRange([start, end]);
    setCallContextRows([{ id: uuidv4(), key: '', value: '' }]);
    setInsightFilterRows([]);
  };

  const updateLocalFilters = (newFilters: Partial<CallFilters>) => {
    setLocalFilters((prev: CallFilters) => ({ ...prev, ...newFilters }));
  };

  useEffect(() => {
    if (isOpen && actions.length === 0) {
      fetchActions();
    }
  }, [isOpen, actions.length, fetchActions]);

  useEffect(() => {
    if (!isOpen) return;

    let cancelled = false;
    const fetchInsights = async () => {
      setInsightsLoading(true);
      try {
        const availableInsights = await listInsights();
        if (cancelled) return;
        setInsights(availableInsights);
      } catch {
        if (cancelled) return;
        notification.error('Failed to load call insights.');
      } finally {
        if (!cancelled) {
          setInsightsLoading(false);
        }
      }
    };

    void fetchInsights();
    return () => {
      cancelled = true;
    };
  }, [isOpen, listInsights, notification]);

  // Sync filters when modal opens
  useEffect(() => {
    if (!isOpen) return;

    setLocalFilters(filters);

    if (filters.call_context) {
      const rows = Object.entries(filters.call_context).map(([key, value]) => ({
        id: uuidv4(),
        key,
        value: String(value),
      }));
      setCallContextRows(
        rows.length > 0 ? rows : [{ id: uuidv4(), key: '', value: '' }],
      );
    } else {
      setCallContextRows([{ id: uuidv4(), key: '', value: '' }]);
    }

    if (filters.post_call_insights?.children) {
      setInsightFilterRows(
        filters.post_call_insights.children.map((child) => ({
          id: uuidv4(),
          insight_id: child.insight_id,
          operator: child.operator,
          right:
            typeof child.value === 'number' ? String(child.value) : child.value,
        })),
      );
    } else {
      setInsightFilterRows([]);
    }
  }, [isOpen, filters]);

  const addCallContextRow = () => {
    setCallContextRows((prev) => [
      ...prev,
      { id: uuidv4(), key: '', value: '' },
    ]);
  };

  const removeCallContextRow = (id: string) => {
    setCallContextRows((prev) => prev.filter((row) => row.id !== id));
  };

  const updateCallContextRow = (
    id: string,
    field: 'key' | 'value',
    value: string,
  ) => {
    setCallContextRows((prev) =>
      prev.map((row) => (row.id === id ? { ...row, [field]: value } : row)),
    );
  };

  const getCallContextJson = (): Record<string, string> => {
    const context: Record<string, string> = {};
    callContextRows.forEach((row) => {
      if (row.key.trim()) {
        context[row.key.trim()] = row.value;
      }
    });
    return context;
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Filter Calls"
      className="lg:max-w-[800px]"
      actionButton={
        <div className="flex gap-2">
          <Button onClick={handleReset} color="default">
            Reset
          </Button>
          <Button onClick={handleApply} disabled={loading}>
            Apply Filters
          </Button>
        </div>
      }
    >
      <div className="space-y-6">
        {/* Date Range */}
        <div>
          <Label htmlFor="period">Period</Label>
          <DateRangePicker
            format="MM/dd/yyyy hh:mm aa"
            showMeridiem
            className="w-full"
            value={dateRange as [Date, Date]}
            onChange={(value) => {
              if (!value) {
                setDateRange([null, null]);
                return;
              }
              setDateRange(value as [Date, Date]);
            }}
            ranges={customRanges as RangeType[]}
          />
        </div>

        {/* Agent and Status Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="agents">Agents</Label>
            <AutoCompleteSelectBox
              key={`agent-${localFilters.agent}`}
              defaultValue={agentsOptions.find(
                (agent) => agent.value === localFilters.agent,
              )}
              fetchOptions={searchAgents}
              onChange={(value) => updateLocalFilters({ agent: value })}
              variant="outlined"
              color="primary"
              size="medium"
              disabled={loading}
              defaultOptions={agentsOptions}
              allowRemoveOption={false}
            />
          </div>

          <div>
            <Label htmlFor="status">Statuses</Label>
            <SelectBox
              options={statusesOptions}
              value={statusesOptions.find(
                (status) => status.value === localFilters.status,
              )}
              variant="outlined"
              color="primary"
              size="medium"
              onChange={(value) => updateLocalFilters({ status: value })}
            />
          </div>
        </div>

        {/* Call ID and Phone Numbers Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Input
              label="Call ID"
              type="text"
              value={localFilters.call_id || ''}
              onChange={(e) => updateLocalFilters({ call_id: e.target.value })}
            />
          </div>

          <div>
            <Input
              label={
                <>
                  Contact Number{' '}
                  <Tooltip
                    content="The number of the contact who received or made the call."
                    zIndex={50}
                  >
                    <Info className="w-4 h-4 inline-block ml-1" />
                  </Tooltip>
                </>
              }
              type="text"
              value={localFilters.contact_number}
              onChange={(e) =>
                updateLocalFilters({ contact_number: e.target.value })
              }
            />
          </div>
        </div>

        {/* Duration Filter */}
        <div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="w-full">
              <Label htmlFor="duration">Duration (seconds)</Label>

              <div className="flex">
                <SelectBox
                  options={durationComparisonOptions}
                  value={durationComparisonOptions.find(
                    (option) =>
                      option.value === localFilters.duration?.comparison,
                  )}
                  variant="outlined"
                  color="primary"
                  size="medium"
                  onChange={(value) =>
                    updateLocalFilters({
                      duration: {
                        comparison: value,
                        value: localFilters.duration?.value || 0,
                      },
                    })
                  }
                  className="w-full rounded-r-none"
                />

                <Input
                  type="number"
                  placeholder="0"
                  value={localFilters.duration?.value || ''}
                  onChange={(e) =>
                    updateLocalFilters({
                      duration: {
                        comparison: localFilters.duration?.comparison || 'gt',
                        value: parseInt(e.target.value) || 0,
                      },
                    })
                  }
                  variant="outlined"
                  color="primary"
                  size="medium"
                  className="w-full rounded-l-none ml-[-1px] py-[0.53rem]"
                  fullWidth={true}
                />
              </div>
            </div>

            {/* Successful Actions Filter */}
            <div className="w-full">
              <Label htmlFor="successful-actions">Successful Actions</Label>
              <AutoCompleteMultiSelectBox
                fetchOptions={fetchActions}
                selectedOptions={selectedActionOptions}
                options={actionOptions}
                onSelectionChange={handleSuccessfulActionsChange}
                variant="outlined"
                color="primary"
                size="medium"
                placeholder="Select completed actions..."
                noOptionsText="No actions found"
                disabled={actionsLoading}
              />
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Transcript Keyword */}
          <div className="w-full">
            <Input
              label="Transcript Keyword"
              type="text"
              placeholder="Search in transcript..."
              value={localFilters.transcript_keyword}
              onChange={(e) =>
                updateLocalFilters({ transcript_keyword: e.target.value })
              }
            />
          </div>

          {/* campaign ref id */}
          <div className="w-full">
            <Label htmlFor="campaign">Campaign</Label>
            <AutoCompleteSelectBox
              key={`campaign-${localFilters.workflow_reference_id}`}
              defaultValue={campaignOptions.find(
                (campaign) =>
                  campaign.value === localFilters.workflow_reference_id,
              )}
              fetchOptions={searchCampaigns}
              onChange={(value) =>
                updateLocalFilters({ workflow_reference_id: value })
              }
              variant="outlined"
              color="primary"
              size="medium"
              disabled={loading}
              defaultOptions={campaignOptions}
              allowRemoveOption={false}
            />
          </div>
        </div>

        {/* Call Insights */}
        <div className="space-y-3">
          <div className="flex items-center justify-between gap-4">
            <div>
              <Label htmlFor="call-insights">Call Insights</Label>
              <div className="text-sm text-neutral-500">
                {insightsLoading
                  ? 'Loading insights...'
                  : insights.length === 0
                    ? 'No call insights available yet.'
                    : insightFilterRows.length === 0
                      ? 'Add a row to filter calls by insight values.'
                      : 'Only calls matching all insight filters are shown.'}
              </div>
            </div>
            <Button
              variant="outlined"
              color="primary"
              size="small"
              onClick={addInsightFilterRow}
              disabled={loading || insightsLoading || insights.length === 0}
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Row
            </Button>
          </div>

          {insightFilterRows.length > 0 && (
            <div className="space-y-2">
              {insightFilterRows.map((row) => (
                <InsightFilterRow
                  key={row.id}
                  insights={insights}
                  selectedInsightId={row.insight_id}
                  operator={row.operator}
                  value={row.right}
                  onInsightChange={(insightId) => {
                    const selectedInsight = getInsightById(insightId);
                    updateInsightFilterRow(row.id, {
                      insight_id: insightId,
                      operator:
                        selectedInsight?.type === InsightType.BOOLEAN
                          ? FieldOperators.EQ
                          : undefined,
                      right: null,
                    });
                  }}
                  onOperatorChange={(operator) =>
                    updateInsightFilterRow(row.id, { operator })
                  }
                  onValueChange={(right) =>
                    updateInsightFilterRow(row.id, {
                      operator: row.operator ?? FieldOperators.EQ,
                      right,
                    })
                  }
                  onDelete={() => removeInsightFilterRow(row.id)}
                  disabled={loading}
                  badgeLabel="Call Insight"
                />
              ))}
            </div>
          )}
        </div>

        {/* Call Context */}
        <div>
          <div className="mb-2 flex items-center justify-between">
            <Label htmlFor="call-context">Call Context</Label>
            <Button
              variant="outlined"
              color="primary"
              size="small"
              onClick={addCallContextRow}
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Row
            </Button>
          </div>
          <div className="space-y-2">
            {callContextRows.map((row) => (
              <div key={row.id} className="flex gap-2 items-start">
                <div className="flex-1">
                  <Input
                    type="text"
                    placeholder="Key"
                    value={row.key}
                    onChange={(e) =>
                      updateCallContextRow(row.id, 'key', e.target.value)
                    }
                    variant="outlined"
                    color="primary"
                    size="medium"
                  />
                </div>
                <div className="flex-1">
                  <Input
                    type="text"
                    placeholder="Value"
                    value={row.value}
                    onChange={(e) =>
                      updateCallContextRow(row.id, 'value', e.target.value)
                    }
                    variant="outlined"
                    color="primary"
                    size="medium"
                  />
                </div>
                <Button
                  variant="text"
                  color="error"
                  size="medium"
                  onClick={() => removeCallContextRow(row.id)}
                  disabled={callContextRows.length === 1}
                  aria-label="Remove row"
                  className="hover:text-white"
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Modal>
  );
};
